import { CmsPageSection, PageTemplate, SuburbBlock } from '../../../../models/content';
import {
  blockEntries,
  END,
  PageTokenValues,
  placeBlocks,
  resolvePageTokens,
  templateOrder,
} from '../template/suburb-template';
import { insertedBlock } from '../template/template-blocks';

/**
 * A suburb page's structure in its Page tab (#262): the template's blocks in order with the blocks inserted on this
 * page between them, and the edits to those inserted blocks (add at a point, move, delete). Pure; the anchor rule is
 * the template's placeBlocks.
 */

/** One line of the outline. */
export interface OutlineRow {
  id: string;
  source: 'template' | 'inserted';
  /** What the block is: Hero, Text, Places near this suburb, … */
  kind: string;
  /** The heading as this page shows it: placeholders filled in, markup dropped. */
  heading: string;
  /** Whether the site shows it on this page (null: not known until the page is saved). */
  shows: boolean | null;
  /** Why it shows or not; on an inserted block, only what follows "Only on <page>" (empty when it shows). */
  reason: string;
  /** The hero's lead as this page shows it. */
  detail?: string;
}

/** Whether a template block shows on this page, and why (the server's blockShows). */
export interface BlockFact {
  shows: boolean;
  reason: string;
  detail?: string;
}
export type BlockFacts = ReadonlyMap<string, BlockFact>;

/** The page's own fields as the tab has them now. */
export interface PageFields {
  intro: string;
  body: string;
  faqIds: ReadonlyArray<string>;
  /** The lead the site writes from the nearest places, when it can (from the server). */
  dataLead: string | null;
}

/**
 * The server's facts (from the last save), with the blocks that read the page's own fields judged again from what the
 * tab has now, so typing an opening paragraph or choosing FAQs shows at once. The same rules as the server's
 * blockShows for those blocks.
 */
export function withFieldFacts(
  template: Pick<PageTemplate, 'blocks'> | null,
  facts: BlockFacts,
  f: PageFields
): Map<string, BlockFact> {
  const out = new Map(facts);
  for (const { id, block, removed } of templateOrder(template)) {
    if (removed) continue;
    if (block.type === 'editorial-hero') {
      const lead = f.intro.trim() || (f.dataLead ?? '').trim();
      const reason = f.intro.trim()
        ? 'the opening paragraph'
        : lead
          ? 'the lead written from the nearest places'
          : 'heading only: no opening paragraph';
      out.set(id, { shows: true, reason, ...(lead ? { detail: lead } : {}) });
    } else if (!block.component && block.bind === 'body') {
      out.set(
        id,
        f.body.trim()
          ? { shows: true, reason: 'the About text' }
          : { shows: false, reason: 'the About text is empty' }
      );
    } else if (block.component === 'faq-overview' && block.bind === 'faqIds') {
      const n = f.faqIds.length;
      out.set(
        id,
        n ? { shows: true, reason: `${n} FAQ(s)` } : { shows: false, reason: 'no FAQs chosen' }
      );
    }
  }
  return out;
}

/** A record's inserted blocks, live ones only, in index order. */
export function insertedList(
  blocks: Record<string, SuburbBlock> | SuburbBlock[] | null | undefined
): SuburbBlock[] {
  return blockEntries(blocks)
    .map(({ block }) => block)
    .filter((b) => (b._meta as { deleted?: boolean }).deleted !== true)
    .sort((a, b) => a._meta.index - b._meta.index);
}

/**
 * The page's lines. An inserted block follows the template's hide rules too (a data block with nothing to show is
 * hidden on the site): the server's facts say so once it is saved; before that a text block shows and a data block
 * isn't known yet.
 */
export function outline(
  template: Pick<PageTemplate, 'blocks'> | null,
  blocks: SuburbBlock[],
  values: PageTokenValues,
  facts: BlockFacts,
  kindOf: (b: Partial<CmsPageSection>) => string
): OutlineRow[] {
  return placeBlocks(template, blocks).map(({ id, source, block }) => {
    const fact =
      facts.get(id) ?? (source === 'inserted' && !block.component ? INSERTED_TEXT : undefined);
    return {
      id,
      source,
      kind: kindOf(block),
      heading: plain(resolvePageTokens(block.heading, values)),
      shows: fact?.shows ?? null,
      reason: source === 'inserted' ? insertedReason(fact) : (fact?.reason ?? ''),
      ...(fact?.detail ? { detail: fact.detail } : {}),
    };
  });
}

const INSERTED_TEXT: BlockFact = { shows: true, reason: '' };

/**
 * What an inserted block's line adds to "Only on <page>": nothing when it shows; why, when it is hidden (the server's
 * reason starts with its own "only on <name>; hidden: "); until when, when it isn't known.
 */
function insertedReason(fact: BlockFact | undefined): string {
  if (!fact) return 'shown or hidden once saved';
  return fact.shows ? '' : `Hidden: ${fact.reason.replace(/^only on [^;]*; hidden: /, '')}`;
}

/** A page's outline as the server builds it from the saved template (the readiness detail's blocks). */
export function outlineFromServer(
  blocks: ReadonlyArray<{
    id: string;
    source: 'template' | 'inserted';
    type: string;
    component: string | null;
    heading: string;
    shows: boolean;
    reason: string;
  }>,
  kindOf: (b: { type?: string; component?: string | null }) => string
): OutlineRow[] {
  return blocks.map((b) => ({
    id: b.id,
    source: b.source,
    kind: kindOf(b),
    heading: plain(b.heading ?? ''),
    shows: b.shows,
    reason: b.source === 'inserted' ? insertedReason(b) : b.reason,
  }));
}

/** What a block is, for the outline: Hero, Text, or its building block's name. */
export function blockKindLabel(
  b: { type?: string; component?: string | null },
  options: ReadonlyArray<{ label: string; value: string }>
): string {
  if (b.type === 'editorial-hero') return 'Hero';
  if (!b.component) return 'Text';
  return options.find((o) => o.value === b.component)?.label ?? b.component;
}

function plain(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

/** A new block after `after` (a template block id, or the end): last among the blocks already placed there. */
export function addBlock(
  blocks: SuburbBlock[],
  after: string,
  section: CmsPageSection
): SuburbBlock[] {
  const index = blocks.reduce((max, b) => Math.max(max, b._meta.index), 0) + 10;
  return [...blocks, { ...insertedBlock(section, after), _meta: { id: section._meta.id, index } }];
}

/** A block edited in the sections editor: its new fields, keeping its place. */
export function replaceBlock(blocks: SuburbBlock[], section: CmsPageSection): SuburbBlock[] {
  return blocks.map((b) =>
    b._meta.id === section._meta.id ? { ...insertedBlock(section, b.after), _meta: b._meta } : b
  );
}

export function removeBlock(blocks: SuburbBlock[], id: string): SuburbBlock[] {
  return blocks.filter((b) => b._meta.id !== id);
}

/**
 * Move an inserted block one line up or down the page (null when it can't: nothing above it but the hero, or nothing
 * below it). Past another inserted block it joins that block's anchor; past a template block it changes its anchor:
 * down, to that block; up, to whatever the line before that block follows. The blocks are then renumbered in page
 * order, which keeps every other block where it is.
 */
export function moveBlock(
  template: Pick<PageTemplate, 'blocks'> | null,
  blocks: SuburbBlock[],
  id: string,
  dir: -1 | 1
): SuburbBlock[] | null {
  const placed = placeBlocks(template, blocks);
  const i = placed.findIndex((p) => p.source === 'inserted' && p.id === id);
  const j = i + dir;
  if (i < 0 || j < 1 || j >= placed.length) return null;
  const anchorOf = (p: (typeof placed)[number]) =>
    p.source === 'inserted' ? (p.block as SuburbBlock).after : p.id;
  const neighbour = placed[j];
  const after =
    neighbour.source === 'inserted' || dir > 0 ? anchorOf(neighbour) : anchorOf(placed[j - 1]);
  const ids = placed.map((p) => p.id);
  [ids[i], ids[j]] = [ids[j], ids[i]];
  const byId = new Map(blocks.map((b) => [b._meta.id, b]));
  const order = ids.filter((x) => byId.has(x));
  return order.map((x, k) => {
    const b = byId.get(x)!;
    return { ...b, after: x === id ? after : b.after, _meta: { ...b._meta, index: (k + 1) * 10 } };
  });
}

/**
 * The blocks as the record stores them (keyed by id). An anchor the template doesn't have goes to the end, which is
 * where the page already shows it (the server refuses an unknown anchor).
 */
export function blocksForSave(
  template: Pick<PageTemplate, 'blocks'> | null,
  blocks: SuburbBlock[]
): Record<string, SuburbBlock> {
  const anchors = new Set([END, ...templateOrder(template).map((t) => t.id)]);
  const out: Record<string, SuburbBlock> = {};
  for (const b of blocks) out[b._meta.id] = anchors.has(b.after) ? b : { ...b, after: END };
  return out;
}
